import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { MdClose, MdOutlineBookmarkAdd } from 'react-icons/md';
import { allowedFilterParams } from '@api/allowedParams';
import {
  useGetBrandNameById,
  useGetModelNameById,
} from '@/src/utils/getBrandById';
import { useGetCountyById } from '@/src/utils/getCountyById';
import QueryLink from '@components/QueryLink';
import { useTranslations } from 'next-intl';
import { filterParamsToQueryString, useSaveSearchMutation } from '@api/api';
import { userState } from '@/src/redux/userSlice/userSlice';
import { useSelector } from 'react-redux';
import { RootState } from '@/src/redux/store';
import Modal from '@components/Modal';
import { useForm } from 'react-hook-form';
import useParamsEditor from '@/src/utils/useParamsEditor';
import getOption from '@/src/utils/getOption';
import ErrorMsg from '@components/ErrorMsg';
import { useGetCategoryById } from '@/src/utils/categories/useGetCategoryById';
import useLocaleSuffix from '@/src/utils/useLocaleSuffix';

export default function ActiveFilters({
  categoryId,
}: {
  categoryId?: number | false | undefined | null;
}) {
  const a = useTranslations('ActiveFilters');
  const t = useTranslations('Filter');
  const g = useTranslations('general');
  const getCategoryById = useGetCategoryById();
  const getLocaleSuffix = useLocaleSuffix();
  const searchParams = useSearchParams();
  const editParam = useParamsEditor();
  const getBrandNameById = useGetBrandNameById();
  const getModelNameById = useGetModelNameById();
  const getCountyById = useGetCountyById();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const [saveModal, saveModalOpen] = useState(false);
  const [saveSearchQuery, { isSuccess, error }] = useSaveSearchMutation();
  const user: userState = useSelector((state: RootState) => {
    return state.user;
  });
  const userId: number | null = useSelector((state: RootState) => {
    return state.user.userId;
  });
  const labelsMap = {
    year_le: `${t('year.label')} <`,
    year_ge: `${t('year.label')} >`,
    price_le: `${t('price.label')} <`,
    price_ge: `${t('price.label')} >`,
    meta_hours_le: `${t('hours.label')} <`,
    meta_hours_ge: `${t('hours.label')} >`,
    meta_km_le: `${t('mileage.label')} <`,
    meta_km_ge: `${t('mileage.label')} >`,
    meta_hk_le: `${t('horsepower.label')} <`,
    meta_hk_ge: `${t('horsepower.label')} >`,
    meta_kg_le: `${t('weight.label')} <`,
    meta_kg_ge: `${t('weight.label')} >`,
    category_id: '',
    category_parent_id: '',
    make: '',
    has_auction: `${t('typesOfSale.label')}`,
    search: `${a('search')}:`,
    zipcode: `${t('area.zip')}:`,
    municipality: `${t('area.municipality')}:`,
  };

  const getSaveQueryString = () => {
    const query = new URLSearchParams(Array.from(searchParams.entries()));
    Array.from(searchParams.entries()).forEach((entry) => {
      if (!allowedFilterParams.includes(entry[0])) {
        query.delete(entry[0]);
      }
    });
    if (categoryId) {
      query.set('category.parent_id', String(categoryId));
    }
    let filterParams = {} as any;
    query.forEach((value: string, key: string) => {
      filterParams[key as keyof typeof filterParams] = value;
    });
    return filterParamsToQueryString(filterParams);
  };

  useEffect(() => {
    if (isSuccess) {
      saveModalOpen(false);
    }
  }, [isSuccess, saveModalOpen]);

  useEffect(() => {
    setValue('user_id', userId);
  }, [userId, setValue]);

  const saveSearch = (formData: any) => {
    saveSearchQuery(formData).then(() => {});
  };

  const getFilters = () => {
    let filters = [] as any[];
    allowedFilterParams.map((param) => {
      if (!searchParams.get(param)) return null;
      const query = new URLSearchParams(Array.from(searchParams.entries()));
      query.delete(param);
      query.delete('page');
      let valueLabel = searchParams.get(param);
      let paramLabel = labelsMap[param as keyof typeof labelsMap];
      if (param === 'search') {
        query.delete('lang');
      }
      if (param === 'has_auction') {
        valueLabel = valueLabel === '1' ? a('auction') : a('fixed');
        paramLabel = '';
      }
      if (
        [
          'municipality',
          'category_id',
          'category_parent_id',
          'county_id',
        ].includes(param)
      ) {
        paramLabel = '';
      }
      if (param === 'category_id') {
        valueLabel = String(searchParams.get(param))
          .split(',')
          .map((cat: string) => {
            const Cat = getCategoryById(parseInt(cat));
            return Cat?.category_name?.[getLocaleSuffix()];
          })
          .join(', ');
      } else if (param === 'make_id') {
        valueLabel = String(searchParams.get(param))
          .split(',')
          .map((brand: string) => {
            return getBrandNameById(parseInt(brand));
          })
          .join(', ');
        query.delete('model_id');
      } else if (param === 'model_id') {
        valueLabel = String(searchParams.get(param))
          .split(',')
          .map((brand: string) => {
            return getModelNameById(parseInt(brand));
          })
          .join(', ');
      } else if (param === 'county_id') {
        valueLabel = String(searchParams.get(param))
          .split(',')
          .map((county: string) => {
            return getCountyById(parseInt(county));
          })
          .join(', ');
      } else if (param === 'auctions') {
        if (String(searchParams.get(param)) === '3,4,8') {
          valueLabel = t('auctions.includeConcluded');
        } else if (String(searchParams.get(param)) === '4,8') {
          valueLabel = t('auctions.onlyConcluded');
        }
      }
      filters.push({
        paramLabel,
        valueLabel,
        queryString: query.toString(),
      });
    });
    return filters;
  };

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {getOption('SHOW_USER_FUNCTIONS') && (
        <button
          onClick={() => {
            saveModalOpen(!saveModal);
            if (user.loggedIn) {
            } else {
              editParam('modal', 'login');
            }
          }}
          className='mr-7 flex cursor-pointer flex-wrap items-center gap-1 rounded-sm border border-brand bg-brand px-3 py-1 font-bold uppercase text-dark transition-all hover:bg-dark hover:text-white'
        >
          <span className='text-xl'>
            <MdOutlineBookmarkAdd />
          </span>
          {a('save')}
        </button>
      )}
      {saveModal && user.loggedIn && (
        <Modal
          customClose={() => {
            saveModalOpen(false);
          }}
        >
          <form
            method='post'
            onSubmit={handleSubmit(saveSearch)}
            className='flex w-[300px] flex-col gap-10'
          >
            <input
              type='hidden'
              {...register('user_id', {
                value: user.userId,
              })}
            />
            <input
              type='hidden'
              {...register('search_query', {
                value: getSaveQueryString(),
              })}
            />
            <div className='flex flex-col gap-4'>
              <label className='mb-2 flex flex-col gap-2 font-semibold uppercase'>
                {a('searchTitle')}
                <input
                  {...register('title', {
                    required: {
                      value: true,
                      message: g('required'),
                    },
                  })}
                  className='px-2 py-1 text-black'
                  placeholder={a('placeholder')}
                ></input>
                <ErrorMsg errors={errors} error={error} name='title' />
              </label>
              <label className='mt-1'>
                <input
                  type='checkbox'
                  {...register('notification')}
                  className='mr-2'
                />
                {a('notifications')}
              </label>
            </div>
            <div className='mt-2 flex flex-wrap gap-3'>
              {getFilters().map(
                ({
                  paramLabel,
                  valueLabel,
                  queryString,
                }: {
                  paramLabel: string;
                  valueLabel: string;
                  queryString: string;
                }) => {
                  return (
                    <span
                      key={queryString}
                      className='rounded border border-white px-4 py-1 text-white'
                    >
                      {paramLabel}{' '}
                      <span className='font-bold'>{valueLabel}</span>
                    </span>
                  );
                }
              )}
            </div>
            <button className='cursor-pointer rounded-sm border border-brand bg-brand px-2 py-2 font-bold uppercase text-dark transition-all hover:bg-transparent hover:text-white'>
              {a('save')}
            </button>
          </form>
        </Modal>
      )}
      <span className='text-lg font-bold uppercase'>{a('label')}</span>
      {getFilters().map(
        ({
          paramLabel,
          valueLabel,
          queryString,
        }: {
          paramLabel: string;
          valueLabel: string;
          queryString: string;
        }) => {
          return (
            <QueryLink
              key={queryString}
              query={queryString}
              className='m-2 flex items-center gap-2 rounded border border-white py-1 pl-4 pr-2 text-white hover:bg-white hover:text-dark'
            >
              {paramLabel} <span className='font-bold'>{valueLabel}</span>
              <span className='text-xl'>
                <MdClose />
              </span>
            </QueryLink>
          );
        }
      )}
    </div>
  );
}
